import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Dropdown, Menu } from 'antd';
import { TbArrowRight } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { CloudStorageConnection, getCloudStorageConnections } from '@/services';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { imgLogoStyling } from '@/theme';
import { ConnectedAccountType, SetStateMethod } from '@/types';
import { formatDate } from '@/utilities';

import { LearnMoreFooter } from '../../layouts';
import DropdownButton from '../DropdownButton';
import CloudStorageConnectionAvatar from './CloudStorageConnectionAvatar';

interface SelectDataSourceDropdownProps {
    block?: boolean;
    children?: ReactNode;
    disabled?: boolean;
    matchTriggerWidth?: boolean;
    selectedConnection: Partial<CloudStorageConnection>;
    setSelectedConnection: SetStateMethod<Partial<CloudStorageConnection>>;
}

interface CloudStorageProviderOption {
    icon: string;
    id: string;
    label: string;
}

interface CloudStorageConnectionTriggerProps {
    block?: boolean;
    disabled?: boolean;
    selectedConnection: Partial<CloudStorageConnection>;
}

export const CloudStorageConnectionTrigger: FC<CloudStorageConnectionTriggerProps> = ({
    block,
    disabled,
    selectedConnection,
}) => {
    return (
        <DropdownButton
            block={block}
            disabled={disabled}
            style={{
                justifyContent: 'flex-start',
                maxWidth: block ? undefined : '14rem',
            }}
        >
            <CloudStorageConnectionAvatar connection={selectedConnection} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedConnection.type ?? 'Select Provider'}
            </span>
        </DropdownButton>
    );
};

export { CloudStorageConnectionAvatar };

const SelectDataSourceDropdown: FC<SelectDataSourceDropdownProps> = ({
    block,
    children,
    disabled,
    matchTriggerWidth,
    selectedConnection,
    setSelectedConnection,
}): JSX.Element => {
    const [existingConnections, setExistingConnections] = useState<CloudStorageConnection[]>([]);
    const [triggerWidth, setTriggerWidth] = useState<number>();
    const setSelectedConnectionRef = useRef(setSelectedConnection);
    const triggerRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        setSelectedConnectionRef.current = setSelectedConnection;
    }, [setSelectedConnection]);

    useEffect(() => {
        let active = true;

        const loadConnections = async () => {
            try {
                const connections = await getCloudStorageConnections();

                if (!active) {
                    return;
                }

                setExistingConnections(connections);
                setSelectedConnectionRef.current((current) => (current.id ? current : (connections[0] ?? {})));
            } catch (error) {
                captureException(error);
            }
        };

        void loadConnections();

        return () => {
            active = false;
        };
    }, []);

    const { token } = useAntDesignTheme();

    const navigate = useNavigate();

    const { integrationLabelToIconMap, globalCloudStorageProviders } = useMaps();

    return (
        <Dropdown
            disabled={disabled}
            trigger={['click']}
            placement='bottomRight'
            onOpenChange={(open) => {
                if (open && matchTriggerWidth) {
                    setTriggerWidth(triggerRef.current?.getBoundingClientRect().width);
                }
            }}
            popupRender={() => (
                <DropdownMenuPanel
                    style={{ width: matchTriggerWidth ? (triggerWidth ?? 16 * 24) : 16 * 24, marginTop: '0.75rem' }}
                    selectedKeys={selectedConnection.id ? [selectedConnection.id] : []}
                    onClick={(e) => {
                        const connection = existingConnections.find((candidate) => candidate.id === e.key);

                        if (connection) {
                            setSelectedConnection(connection);
                        }
                    }}
                >
                    {existingConnections?.length !== 0 && (
                        <>
                            <div>
                                <small
                                    style={{
                                        marginLeft: '0.75rem',
                                        color: token.colorTextTertiary,
                                        verticalAlign: '0.2rem',
                                    }}
                                >
                                    Active Cloud Storages
                                </small>
                            </div>
                            {existingConnections.map((ec) => {
                                return (
                                    <Menu.Item key={ec?.id}>
                                        <Avatar
                                            style={{
                                                backgroundColor: token.colorBgBase,
                                                border: `1px solid ${token.colorBorder}`,
                                                marginRight: '0.5rem',
                                            }}
                                            size={24}
                                            shape='square'
                                            icon={
                                                <img
                                                    alt={ec?.label}
                                                    src={integrationLabelToIconMap[ec?.type as ConnectedAccountType]}
                                                    style={{
                                                        ...imgLogoStyling,
                                                        transform: 'scale(0.75)',
                                                    }}
                                                />
                                            }
                                        />
                                        <span style={{ verticalAlign: '-0.075rem' }}>{ec?.type && ec?.type}</span>
                                        <br />
                                        <small style={{ color: token.colorTextTertiary }}>
                                            Connected {ec.createdAt && formatDate(new Date(ec.createdAt))}
                                        </small>
                                    </Menu.Item>
                                );
                            })}
                            <Menu.Divider />
                        </>
                    )}
                    <div>
                        <small
                            style={{
                                marginLeft: '0.75rem',
                                color: token.colorTextTertiary,
                                verticalAlign: '0.2rem',
                            }}
                        >
                            Add a Storage Connection
                        </small>
                    </div>
                    {(globalCloudStorageProviders as CloudStorageProviderOption[] | undefined)?.map((csp) => {
                        return (
                            <Menu.Item
                                key={csp?.id}
                                onClick={() => {
                                    navigate(`/settings/account/general/connections`);
                                }}
                            >
                                <Avatar
                                    style={{
                                        backgroundColor: token.colorBgBase,
                                        border: `1px solid ${token.colorBorder}`,
                                        marginRight: '0.5rem',
                                    }}
                                    size={24}
                                    shape='square'
                                    icon={
                                        <img
                                            alt={csp?.label}
                                            src={csp?.icon}
                                            style={{
                                                ...imgLogoStyling,
                                                transform: 'scale(0.75)',
                                            }}
                                        />
                                    }
                                />
                                <span style={{ verticalAlign: '-0.075rem' }}>{csp?.label}</span>
                                <TbArrowRight
                                    style={{
                                        color: token.colorTextTertiary,
                                        verticalAlign: '-0.2rem',
                                        marginLeft: '0.5rem',
                                    }}
                                />
                            </Menu.Item>
                        );
                    })}
                    <LearnMoreFooter topic='uploads' variant='dropdown' />
                </DropdownMenuPanel>
            )}
        >
            <span ref={triggerRef} style={{ display: block ? 'block' : undefined, width: block ? '100%' : undefined }}>
                {children}
            </span>
        </Dropdown>
    );
};

export default SelectDataSourceDropdown;
